import type {PermissionGroup, PermissionRow} from '../utils';
import {groupPermissionRows, riskLabels} from '../utils';
import {Icon} from './Icon';

interface Props {
  rows: PermissionRow[];
  showScopes?: boolean;
  showOrigin?: boolean;
  /** Mode sélection : une case par ligne, pour composer une version de profil. */
  selection?: {selected: Set<string>; onToggle(permission: string, checked: boolean): void; disabled?: boolean};
  openFirst?: boolean;
}

/**
 * Permissions en table groupée par outil : code, risque, périmètres, ce qu'elle autorise, origine.
 * Remplace la liste brute de codes et la grille `.permission-columns` qui débordait.
 */
export function PermissionTable({rows, showScopes = true, showOrigin = false, selection, openFirst = true}: Props) {
  const groups = groupPermissionRows(rows);
  if (!groups.length) return <div className="empty">Aucune permission.</div>;
  return <div className="x-perm-groups">
    {groups.map((group, index) => <PermissionGroupTable
      key={group.module}
      group={group}
      open={openFirst ? index === 0 : false}
      showScopes={showScopes}
      showOrigin={showOrigin}
      selection={selection}
    />)}
  </div>;
}

function PermissionGroupTable({group, open, showScopes, showOrigin, selection}: {
  group: PermissionGroup; open: boolean; showScopes: boolean; showOrigin: boolean; selection: Props['selection'];
}) {
  const chosen = selection ? group.rows.filter(row => selection.selected.has(row.permission)).length : 0;
  return <details className="pgroup" open={open || (!!selection && chosen > 0)}>
    <summary>
      <span>{group.label}</span>
      <span className="muted">{selection ? `${chosen} sur ${group.rows.length} permissions` : `${group.rows.length} permission${group.rows.length > 1 ? 's' : ''}`}</span>
      <Icon name="down" size={14} />
    </summary>
    <div className="tbl-wrap">
      <table className={`tbl perms${selection ? ' select' : ''}`}>
        <thead><tr>
          {selection && <th><span className="sr-only">Sélection</span></th>}
          <th>Permission</th>
          <th>Risque</th>
          {showScopes && <th>Périmètres</th>}
          <th>Ce qu’elle autorise</th>
          {showOrigin && <th>Origine</th>}
        </tr></thead>
        <tbody>
          {group.rows.map(row => <tr key={row.key}>
            {selection && <td><input
              type="checkbox"
              aria-label={row.permission}
              checked={selection.selected.has(row.permission)}
              disabled={selection.disabled}
              onChange={event => selection.onToggle(row.permission, event.target.checked)}
            /></td>}
            <td><code>{row.permission}</code></td>
            <td><span className={`chip rk-${row.risk}`}>{riskLabels[row.risk]}</span></td>
            {showScopes && <td className="muted">{row.scopes}</td>}
            <td>{row.description}</td>
            {showOrigin && <td className="muted origin">{row.origin || '—'}</td>}
          </tr>)}
        </tbody>
      </table>
    </div>
  </details>;
}
